/*
 * Inbox v2's calls to the API (`src/inbox-v2/routes.ts`). Every answer is parsed through the shared
 * contract before the screen sees it, so a field the server never promised cannot reach the page.
 */
import { z } from "zod";
import {
  type MailboxMessage,
  MailboxMessageSchema,
  type MailboxPage,
  type MailboxPageInput,
  MailboxPageSchema,
} from "../../../../skills/leadcue-reply/scripts/lib/bison-mail-contracts.ts";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import {
  type AgentStatus,
  AgentStatusSchema,
  InboxAgentLimitErrorSchema,
  type InboxAgentList,
  InboxAgentListSchema,
} from "../../src/inbox-v2/agent-contract.ts";
import {
  type InboxAccess,
  InboxAccessSchema,
  type InboxAssignees,
  InboxAssigneesSchema,
  InboxAssignResultSchema,
  InboxBackfillSchema,
  InboxBulkResultSchema,
  type InboxCounts,
  InboxCountsSchema,
  InboxDoneResultSchema,
  InboxDraftsPageSchema,
  InboxLabelCodeSchema,
  type InboxListQuery,
  InboxNeedsReplySchema,
  type InboxPage,
  InboxPageSchema,
  InboxRevisionSchema,
  type InboxSearchStatus,
  InboxSearchStatusSchema,
  type InboxSendRequest,
  type InboxSendStatus,
  InboxSendStatusSchema,
  InboxSnoozeResultSchema,
  InboxSuggestionsSchema,
  type InboxThread,
  InboxThreadSchema,
  InboxViewSchema,
  InboxViewsSchema,
  type ReplyFileSelection,
  ReplyFileSelectionSchema,
  type ReplyFilesStatus,
  ReplyFilesStatusSchema,
  type SendSettlementRequest,
} from "../../src/inbox-v2/contract.ts";
import type { InboxDraftSendRequest } from "../../src/inbox-v2/draft-send-contract.ts";
import { ForwardCompositionSchema } from "../../src/inbox-v2/forward-contract.ts";
import { type ReplySender, ReplySenderSchema } from "../../src/mailbox-reply-signature-contract.ts";
import {
  type BounceMessage,
  BounceMessageSchema,
  type BouncesPage,
  BouncesPageSchema,
  type MoreFolderPage,
  MoreFolderPageSchema,
  type MoreMessage,
  MoreMessageSchema,
  type PlainMoreFolder,
} from "../../src/message-index/contract.ts";
import { type ImportState, InboxImportStatusSchema } from "../../src/workspace-import.ts";
import { ApiError, failureReferenceOf, getJson } from "../http.ts";

// A future server can add statuses while this bundle is still open. Keep known totals exact.
const AgentListReadSchema = InboxAgentListSchema.extend({
  counts: z
    .record(z.string(), z.unknown())
    .transform((counts) =>
      Object.fromEntries(AgentStatusSchema.options.map((status) => [status, counts[status]])),
    )
    .pipe(InboxAgentListSchema.shape.counts),
}).strip();

// A rollback can serve the previous thread even when this bundle asks for history=1.
const ThreadReadSchema = InboxThreadSchema.extend({
  messages: z.array(
    InboxThreadSchema.shape.messages.element.extend({
      intent: InboxThreadSchema.shape.messages.element.shape.intent.default(null),
    }),
  ),
  changes: z
    .array(
      InboxThreadSchema.shape.changes.element.extend({
        confirmed: z.boolean().default(false),
        actorId: z.string().nullable().default(null),
      }),
    )
    .transform((changes) =>
      changes.map((change, index) => ({ ...change, id: change.id ?? `legacy:${index}` })),
    ),
});

/** The meeting route's answer (`src/inbox-labels/meeting-mark.ts`): the flag and label it left. */
const MeetingMarkResultSchema = z.strictObject({
  meeting: z.boolean(),
  label: InboxLabelCodeSchema.nullable(),
  changed: z.boolean(),
});

/** A saved view's stored query: what the list asks for, without paging. */
export type ViewQuery = Omit<Partial<InboxListQuery>, "offset" | "section" | "expand">;

/** What the reply agent filter asks: the list's own narrowing and master status, newest first. */
export type AgentListQuery = Omit<Partial<InboxListQuery>, "sort" | "section"> & {
  agent: AgentStatus;
};

/**
 * The list narrowed to a reply agent status, or the server's refusal to count past its limit, which
 * the list shows in its own place: an exact count or none, never an estimate.
 */
export type AgentListAnswer =
  | ({ kind: "listed" } & InboxAgentList)
  | { kind: "refused"; limit: number; message: string };

/**
 * A write the API refused (`ApiError`'s kinds). A write's network failure is not one: it stays the
 * fetch's own error, so a send whose outcome is unknown is never read as refused.
 */
export class InboxApiError extends ApiError {
  constructor(status: number, body: unknown = null) {
    super(status, body);
    this.name = "InboxApiError";
  }
}

/** A POST with a body; PUT and DELETE by name. */
async function call(path: string, body?: object, method?: "PUT" | "DELETE"): Promise<unknown> {
  const response = await fetch(path, {
    method: method ?? "POST",
    credentials: "same-origin",
    ...(body === undefined
      ? {}
      : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
  });
  if (!response.ok)
    throw new InboxApiError(response.status, await response.json().catch(() => null));
  return response.json();
}

/** New send fields are explicitly negotiated so an already-open strict client can keep sending replies. */
async function sendCall(
  path: string,
  body?: object,
  signal?: AbortSignal,
): Promise<InboxSendStatus> {
  const response = await fetch(path, {
    method: body ? "POST" : "GET",
    credentials: "same-origin",
    headers: {
      "x-leadcue-send-view": "selected-forward",
      ...(body ? { "content-type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok)
    throw new InboxApiError(response.status, await response.json().catch(() => null));
  return InboxSendStatusSchema.parse(await response.json());
}

/** The server's reference for a failed read or write, for the notice to quote; null if it gave none. */
export function failureReference(error: unknown): string | null {
  return failureReferenceOf(error);
}

const base = (workspaceId: string) => `/api/work/workspaces/${encodeURIComponent(workspaceId)}`;
/** A conversation's reply-box files (`reply-attachment-routes.ts`). */
const files = (workspaceId: string, planId: string) =>
  `${base(workspaceId)}/plans/${encodeURIComponent(planId)}/reply-files`;

/**
 * Every list and count read asks for the assignee (card A4), and a thread read where assignment is
 * known to be on. Where it is off, and on a server from before it, the answer is the shape it always
 * was, so asking costs no extra request.
 */
const ASSIGNEE = "assignee";

/**
 * Meeting requests (LEA-494): every list, count, thread and agent read asks with `meet=1`, so every
 * row carries the meeting flag and Needs reply leaves out the meeting requests, which the rail lists
 * in their own row. "Who answers next" reads Meeting requests first instead (`nextPlace`).
 */
const MEET = "1";

/** A query's set values as URL parameters; an empty one is left out. */
function params(query: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  return search.toString();
}

export const inboxApi = {
  mailPage: async (
    workspaceId: string,
    input: MailboxPageInput,
    signal?: AbortSignal,
  ): Promise<MailboxPage> =>
    MailboxPageSchema.parse(await getJson(`${base(workspaceId)}/mail?${params(input)}`, signal)),
  mailMessage: async (
    workspaceId: string,
    ref: string,
    signal?: AbortSignal,
  ): Promise<MailboxMessage> =>
    MailboxMessageSchema.parse(
      await getJson(`${base(workspaceId)}/mail/${encodeURIComponent(ref)}`, signal),
    ),
  access: async (workspaceId: string, signal?: AbortSignal): Promise<InboxAccess> =>
    InboxAccessSchema.parse(await getJson(`${base(workspaceId)}/access`, signal)),
  list: async (
    workspaceId: string,
    query: Partial<InboxListQuery>,
    signal?: AbortSignal,
  ): Promise<InboxPage> =>
    InboxPageSchema.parse(
      await getJson(
        `${base(workspaceId)}/inbox?${params({ ...query, meet: MEET, with: ASSIGNEE, dataRequest: "1" })}`,
        signal,
      ),
    ),
  /** Step 9: needs the reply agent; the totals beside each status ignore the one asked for. */
  agentList: async (
    workspaceId: string,
    query: AgentListQuery,
    signal?: AbortSignal,
  ): Promise<AgentListAnswer> => {
    try {
      const answer = await getJson(
        `${base(workspaceId)}/inbox/agent/list?${params({ ...query, meet: MEET, dataRequest: "1" })}`,
        signal,
      );
      return { kind: "listed", ...AgentListReadSchema.parse(answer) };
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== "agent_status_candidate_limit")
        throw error;
      const refusal = InboxAgentLimitErrorSchema.parse(error.body);
      return { kind: "refused", limit: refusal.limit, message: refusal.message };
    }
  },
  /**
   * The campaigns whose history is still importing, none once all have finished. Needs the reply
   * agent (`inbox_reply_agent`): only agent holders onboard a campaign.
   */
  backfill: async (workspaceId: string, signal?: AbortSignal) =>
    InboxBackfillSchema.parse(await getJson(`${base(workspaceId)}/inbox/backfill`, signal))
      .backfill,
  /** One page of the Bounces folder (LEA-311): Hard or Soft, or every bounce. */
  bounces: async (
    workspaceId: string,
    query: { kind: "hard" | "soft" | null; page: number },
    signal?: AbortSignal,
  ): Promise<BouncesPage> => {
    const search = new URLSearchParams({ page: String(query.page) });
    if (query.kind) search.set("kind", query.kind);
    return BouncesPageSchema.parse(
      await getJson(`${base(workspaceId)}/bounces?${search.toString()}`, signal),
    );
  },
  /** One bounce, its text read from Bison as it opens. */
  bounce: async (workspaceId: string, ref: string, signal?: AbortSignal): Promise<BounceMessage> =>
    BounceMessageSchema.parse(
      await getJson(`${base(workspaceId)}/bounces/${encodeURIComponent(ref)}`, signal),
    ),
  /** One page of another More folder (LEA-314): Auto-replies, Other campaigns, Spam, Untracked. */
  moreFolder: async (
    workspaceId: string,
    folder: PlainMoreFolder,
    page: number,
    signal?: AbortSignal,
  ): Promise<MoreFolderPage> =>
    MoreFolderPageSchema.parse(
      await getJson(`${base(workspaceId)}/${folder}?page=${page}`, signal),
    ),
  /** One message of such a folder, its text read from Bison as it opens. */
  moreMessage: async (
    workspaceId: string,
    folder: PlainMoreFolder,
    ref: string,
    signal?: AbortSignal,
  ): Promise<MoreMessage> =>
    MoreMessageSchema.parse(
      await getJson(`${base(workspaceId)}/${folder}/${encodeURIComponent(ref)}`, signal),
    ),
  /**
   * With the master inbox's own totals: Needs reply and Snoozed, which the rail shows. An owner
   * narrows every total to that person's threads, `me` or `none` included (the rail's scope).
   */
  counts: async (
    workspaceId: string,
    signal?: AbortSignal,
    owner: string | null = null,
  ): Promise<InboxCounts> =>
    InboxCountsSchema.parse(
      await getJson(
        `${base(workspaceId)}/inbox/counts?expand=list&trash=1&follow=1&meet=1&drafts=1&with=${ASSIGNEE}${
          owner ? `&assigned=${encodeURIComponent(owner)}` : ""
        }`,
        signal,
      ),
    ),
  /**
   * One page of Drafts (LEA-513): the conversations holding this person's drafts, newest edit first,
   * each as the inbox shows it. Refused (403) where Drafts is not released.
   */
  drafts: async (workspaceId: string, cursor: string | null, signal?: AbortSignal) =>
    InboxDraftsPageSchema.parse(
      await getJson(
        `${base(workspaceId)}/drafts?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
        signal,
      ),
    ),
  /** This viewer's inbox revision here: it moves when anything the inbox shows may have (W5). */
  revision: async (workspaceId: string, signal?: AbortSignal): Promise<string> =>
    InboxRevisionSchema.parse(await getJson(`${base(workspaceId)}/inbox/revision`, signal))
      .revision,
  /** Whether search covers the whole list here, so an empty result can say what it means. */
  searchStatus: async (workspaceId: string, signal?: AbortSignal): Promise<InboxSearchStatus> =>
    InboxSearchStatusSchema.parse(
      await getJson(`${base(workspaceId)}/inbox/search-status`, signal),
    ),
  /**
   * Whether this workspace's last 90 days of mail are still importing, as campaigns done of total
   * (LEA-483). Needs only `inbox`: everyone who reads the list may be told it is incomplete.
   */
  importStatus: async (workspaceId: string, signal?: AbortSignal): Promise<ImportState> =>
    InboxImportStatusSchema.parse(await getJson(`${base(workspaceId)}/inbox/import-status`, signal))
      .import,
  /** Work needing this person's reply, independently of whether they opened it. */
  needsReply: async (workspaceId: string, signal?: AbortSignal): Promise<number> =>
    InboxNeedsReplySchema.parse(await getJson(`${base(workspaceId)}/inbox/needs-reply`, signal))
      .needsReply,
  /** This person's own saved views of the inbox, in their order. */
  views: async (workspaceId: string, signal?: AbortSignal) =>
    InboxViewsSchema.parse(await getJson(`${base(workspaceId)}/inbox/views`, signal)).views,
  /** Operators the typeahead offers for the word being typed: contacts, companies, campaigns. */
  suggest: async (workspaceId: string, word: string, signal?: AbortSignal) =>
    InboxSuggestionsSchema.parse(
      await getJson(`${base(workspaceId)}/inbox/suggest?q=${encodeURIComponent(word)}`, signal),
    ).suggestions,
  createView: async (workspaceId: string, name: string, query: ViewQuery) =>
    InboxViewSchema.parse(await call(`${base(workspaceId)}/inbox/views`, { name, query })),
  updateView: async (
    workspaceId: string,
    id: string,
    change: { name?: string; query?: ViewQuery },
  ) =>
    InboxViewSchema.parse(
      await call(`${base(workspaceId)}/inbox/views/${encodeURIComponent(id)}`, change, "PUT"),
    ),
  deleteView: async (workspaceId: string, id: string) =>
    call(`${base(workspaceId)}/inbox/views/${encodeURIComponent(id)}`, undefined, "DELETE"),
  /** Until when each conversation slept before, so Undo can put each back as it was. */
  snooze: async (workspaceId: string, planIds: readonly string[], until: string) =>
    InboxSnoozeResultSchema.parse(
      await call(`${base(workspaceId)}/inbox/snooze`, { planIds, until }),
    ).previous,
  unsnooze: async (workspaceId: string, planIds: readonly string[]) =>
    InboxSnoozeResultSchema.parse(await call(`${base(workspaceId)}/inbox/unsnooze`, { planIds }))
      .previous,
  /** A thread, with its assignment where assignment is known to be on here (card A4). */
  thread: async (
    workspaceId: string,
    planId: string,
    signal?: AbortSignal,
    withAssignee = false,
  ): Promise<InboxThread> =>
    ThreadReadSchema.parse(
      await getJson(
        `${base(workspaceId)}/plans/${encodeURIComponent(planId)}/inbox?history=1&meet=1&seenBy=1&dataRequest=1&bounces=1${withAssignee ? `&with=${ASSIGNEE}&assignmentRecord=1` : ""}`,
        signal,
        { "x-leadcue-forward-history": "1" },
      ),
    ),
  /**
   * Who a reply from this mailbox comes from: the sender's name and the signature the reply box
   * shows (docs/2026-11-03-reply-box-mailbox-signature.md).
   */
  replySender: async (
    workspaceId: string,
    address: string,
    signal?: AbortSignal,
  ): Promise<ReplySender> =>
    ReplySenderSchema.parse(
      await getJson(
        `${base(workspaceId)}/reply-sender?${new URLSearchParams({ address }).toString()}`,
        signal,
      ),
    ),
  assignees: async (workspaceId: string, signal?: AbortSignal): Promise<InboxAssignees> =>
    InboxAssigneesSchema.parse(await getJson(`${base(workspaceId)}/inbox/assignees`, signal)),
  /**
   * Assign threads to a person, or to nobody (`userId` null). `expectedRevision`, for one thread,
   * refuses with 409 `assignment_changed` if it was reassigned since the pane read it.
   */
  assign: async (
    workspaceId: string,
    planIds: readonly string[],
    userId: string | null,
    expectedRevision?: number,
  ) =>
    InboxAssignResultSchema.parse(
      await call(`${base(workspaceId)}/inbox/assign`, {
        planIds,
        userId,
        ...(expectedRevision === undefined ? {} : { expectedRevision }),
      }),
    ),
  done: async (workspaceId: string, planIds: readonly string[], done: boolean) =>
    InboxDoneResultSchema.parse(await call(`${base(workspaceId)}/inbox/done`, { planIds, done })),
  /** Delete into Trash, or restore from it (LEA-457); LeadCue only, never Bison. */
  trash: async (workspaceId: string, planIds: readonly string[], trashed: boolean) =>
    InboxBulkResultSchema.parse(
      await call(`${base(workspaceId)}/inbox/trash`, { planIds, trashed }),
    ).previous,
  seen: async (workspaceId: string, planIds: readonly string[], seen: boolean) =>
    InboxBulkResultSchema.parse(await call(`${base(workspaceId)}/inbox/seen`, { planIds, seen }))
      .previous,
  relabel: async (
    workspaceId: string,
    items: readonly { planId: string; messageId: string }[],
    label: InboxLabelCode | null,
  ) =>
    z
      .object({ refused: z.array(z.string()), events: z.array(z.string()).default([]) })
      .parse(await call(`${base(workspaceId)}/inbox/label`, { items, label })),
  /** Undo of a bulk relabel: `kept` are the events someone relabelled over since, left as they are. */
  undoRelabel: async (workspaceId: string, events: readonly string[]) =>
    z
      .object({ withdrawn: z.array(z.string()), kept: z.array(z.string()) })
      .parse(await call(`${base(workspaceId)}/inbox/label/undo`, { events })),
  /**
   * The rep's word on whether the newest lead message asks for a meeting (LEA-494). Yes on a thread
   * not labelled Interested labels it Interested too; 409 `stale_message` when a newer lead message
   * has come in, 404 `not_found` for a thread that is not here.
   */
  meeting: async (workspaceId: string, planId: string, messageId: string, meeting: boolean) =>
    MeetingMarkResultSchema.parse(
      await call(`${base(workspaceId)}/plans/${encodeURIComponent(planId)}/meeting`, {
        messageId,
        meeting,
      }),
    ),
  format: async (workspaceId: string, planId: string, format: "text" | "html") =>
    call(`${base(workspaceId)}/plans/${encodeURIComponent(planId)}/reply-format`, { format }),
  forwardComposition: async (
    workspaceId: string,
    planId: string,
    source: string,
    signal?: AbortSignal,
  ) =>
    ForwardCompositionSchema.parse(
      await getJson(
        `${base(workspaceId)}/plans/${encodeURIComponent(planId)}/reply-forward?source=${encodeURIComponent(source)}`,
        signal,
      ),
    ),
  sendStatus: async (
    workspaceId: string,
    planId: string,
    signal?: AbortSignal,
    source?: string,
  ): Promise<InboxSendStatus> =>
    sendCall(
      `${base(workspaceId)}/plans/${encodeURIComponent(planId)}/reply-send${source ? `?source=${encodeURIComponent(source)}` : ""}`,
      undefined,
      signal,
    ),
  send: async (
    workspaceId: string,
    planId: string,
    request: InboxSendRequest | InboxDraftSendRequest,
  ): Promise<InboxSendStatus> =>
    sendCall(
      `${base(workspaceId)}/plans/${encodeURIComponent(planId)}/${"draft" in request ? "reply-draft-send" : "reply-send"}`,
      request,
    ),
  forward: async (
    workspaceId: string,
    planId: string,
    request: InboxSendRequest,
  ): Promise<InboxSendStatus> =>
    sendCall(`${base(workspaceId)}/plans/${encodeURIComponent(planId)}/reply-forward`, request),
  undoSend: async (workspaceId: string, planId: string, id: string): Promise<InboxSendStatus> =>
    sendCall(`${base(workspaceId)}/plans/${encodeURIComponent(planId)}/reply-send/undo`, { id }),
  settleSend: async (
    workspaceId: string,
    planId: string,
    settlement: SendSettlementRequest,
  ): Promise<InboxSendStatus> =>
    sendCall(
      `${base(workspaceId)}/plans/${encodeURIComponent(planId)}/reply-send/settle`,
      settlement,
    ),
  replyFiles: async (
    workspaceId: string,
    planId: string,
    composerId: string,
    signal?: AbortSignal,
  ): Promise<ReplyFilesStatus> =>
    ReplyFilesStatusSchema.parse(
      await getJson(`${files(workspaceId, planId)}?${params({ composer: composerId })}`, signal),
    ),
  /** One file as the request's body, its name and the box's revision in headers (`reply-attachment-routes.ts`). */
  uploadReplyFile: async (
    workspaceId: string,
    planId: string,
    upload: {
      composerId: string;
      uploadId: string;
      revision: number;
      messageId: string;
      forward?: boolean;
      file: File;
      signal?: AbortSignal;
    },
  ): Promise<ReplyFileSelection> => {
    const response = await fetch(files(workspaceId, planId), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "content-type": upload.file.type || "application/octet-stream",
        "x-leadcue-reply-composer": upload.composerId,
        "x-leadcue-reply-upload": upload.uploadId,
        "x-leadcue-reply-revision": String(upload.revision),
        "x-leadcue-reply-message": upload.messageId,
        ...(upload.forward ? { "x-leadcue-compose-intent": "forward" } : {}),
        "x-leadcue-reply-file-name": encodeURIComponent(upload.file.name),
      },
      body: upload.file,
      ...(upload.signal ? { signal: upload.signal } : {}),
    });
    if (!response.ok)
      throw new InboxApiError(response.status, await response.json().catch(() => null));
    return z.object({ selection: ReplyFileSelectionSchema }).parse(await response.json()).selection;
  },
  removeReplyFile: async (
    workspaceId: string,
    planId: string,
    composerId: string,
    fileId: string,
    revision: number,
  ): Promise<ReplyFileSelection> =>
    z
      .object({ selection: ReplyFileSelectionSchema })
      .parse(
        await call(
          `${files(workspaceId, planId)}/${encodeURIComponent(fileId)}?${params({ composer: composerId, revision })}`,
          undefined,
          "DELETE",
        ),
      ).selection,
  /** The box takes a saved draft's files, replacing what it held. */
  adoptDraftFiles: async (
    workspaceId: string,
    planId: string,
    input: {
      composerId: string;
      expectedRevision: number;
      messageId: string;
      draftRevision: number;
    },
  ): Promise<ReplyFileSelection> =>
    z
      .object({ selection: ReplyFileSelectionSchema })
      .parse(await call(`${files(workspaceId, planId)}/from-draft`, input)).selection,
  /** A download of one of the box's files: an attachment, never shown in the page. */
  replyFileHref: (workspaceId: string, planId: string, fileId: string) =>
    `${files(workspaceId, planId)}/${encodeURIComponent(fileId)}`,
};
